import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import * as Routes from '@/modules/routes';
import Spinner from '@/components/common/Spinner';
import AIReadingLevelGenerating from '@/components/shared/AIReadingLevelGenerating';
import { AI_GRADE_BANDS, type GradeBand } from '@/components/shared/TextControlsToolbar/AIReadingLevelButton/constants';
import useSlideShowContext from '@/components/admin/SlideShow/stores/useSlideShowContext';
import useDragAndDropStore from '@/components/common/Forms/Questions/DragAndDrop/useDragAndDropStore';
import TextHighlighter from '@/components/common/TextHighlighter';
import DraggableToolbar from './DraggableToolbar/DraggableToolbar';
import ReadingSelectModal from './ReadingSelectModal';
import { SlideObject } from '../../../types';
import {
  initializeOldBuilderGlossaryTerms,
  loadNewBuilderGlossaryTerms,
  handleTextSectionClick,
} from './readingSectionUtils';

// AIDEV-NOTE: PLAT-18017 — mirror of MainIdeas::SELECTOR in lib/main_ideas.rb; keep in step. Parses
// rather than substring-matching so prose containing the words can't fake a button that toggles nothing.
const MAIN_IDEA_SELECTOR = '.main-idea, .mainideas';

const hasMainIdeasIn = (html?: string) => (
  !!html && !!new DOMParser().parseFromString(html, 'text/html').querySelector(MAIN_IDEA_SELECTOR)
);

interface SlideObjectReadingProps {
  interactable: boolean;
  modalManager: ModalManager;
  slideObject: SlideObject;
}

const SlideObjectReadingSection = ({ interactable, modalManager, slideObject }: SlideObjectReadingProps) => {
  const isPresentMode = useSlideShowContext(state => state.isPresentMode);
  const locale = useSlideShowContext(state => state.locale);
  const programId = useSlideShowContext(state => state.programId);
  const setReadingLoaded = useSlideShowContext(state => state.setReadingLoaded);
  const programGradeBands = useSlideShowContext(state => state.programGradeBands);
  const readingLevelAEnabled = useSlideShowContext(state => state.readingLevelAEnabled);
  const studentLeveledReading = useSlideShowContext(state => state.studentLeveledReading);
  const userId = useSlideShowContext(state => state.userId);
  const userType = useSlideShowContext(state => state.userType);
  const resizerScaleValue = useDragAndDropStore(state => state.resizerScaleValue);
  const sectionRef = useRef<HTMLDivElement>(null);
  const [slideEl, setSlideEl] = useState<Element | null>(null);
  const [glossaryReady, setGlossaryReady] = useState(false);

  // AIDEV-NOTE: PLAT-16897 — readingLevel is owned here so DraggableToolbar + content area share
  // the same selected value. Starts at 'a' (standard); changes when teacher picks a grade band.
  const [readingLevel, setReadingLevel] = useState<string>('a');

  const queryClient = useQueryClient();

  const { data: sectionText, isLoading: isLoadingText } = useQuery(
    ['sectionId', slideObject.modelId, readingLevel],
    () => axios
      .get(Routes.plato_api_get_reading_level_text_section_path(slideObject.modelId), {
        params: {
          locale,
          reading_level: readingLevel,
        },
      })
      .then(response => response.data),
    {
      // Return null (not throw) for 404 — used to detect "content not yet generated".
      retry: false,
    },
  );

  const hasMainIdeas = hasMainIdeasIn(sectionText?.section_reading_level_translation?.body_html);
  const translationId = sectionText?.section_reading_level_translation?.id;

  const { data: highlights, isLoading: isLoadingHighlights } = useQuery(
    ['highlights', slideObject.modelId, translationId, userId],
    () => axios
      .get(Routes.plato_api_highlights_path(), {
        params: {
          search: {
            model_id_eq: translationId,
            model_type_eq: 'SectionReadingLevelTranslation',
          },
        },
      })
      .then(response => response.data?.data || []),
    {
      cacheTime: 0,
      enabled: !!translationId && !!userId && !!sectionText,
    },
  );

  // AIDEV-NOTE: In present mode, portal the toolbar into `.slide` so its offsetParent is the slide
  // (not the slide-object anchor) — keeps react-draggable's bounds tied to the full slide.
  useLayoutEffect(() => {
    if (sectionText && isPresentMode) setSlideEl(sectionRef.current?.closest('.slide') ?? null);
    else setSlideEl(null);
  }, [sectionText, isPresentMode]);

  // AIDEV-NOTE: Run glossary DOM mutations before DraggableToolbar mounts — SpeechStream
  // indexes the page once on load, so a stale DOM at that point breaks playback past the
  // first sentence.
  useEffect(() => {
    if (isLoadingText || !sectionText) return;

    setReadingLoaded(true);
    initializeOldBuilderGlossaryTerms();
    loadNewBuilderGlossaryTerms();
    setGlossaryReady(true);
  }, [isLoadingText, sectionText, setReadingLoaded]);

  useEffect(() => () => {
    setReadingLoaded(false);
  }, [setReadingLoaded]);

  const isStaffer = userType !== 'Student';
  const missingContent = !isLoadingText && !sectionText && readingLevel !== 'a';
  const isAiGradeBand = AI_GRADE_BANDS.includes(readingLevel as GradeBand);

  if (isLoadingText) return <Spinner />;

  // AIDEV-NOTE: When content is missing for the selected level, always render the toolbar so
  // the user can switch back. AI bands additionally show the generation UI.
  if (missingContent) {
    return (
      <>
        <DraggableToolbar
          hasMainIdeas={hasMainIdeas}
          isStaffer={isStaffer}
          locale={locale}
          programGradeBands={programGradeBands}
          programId={programId}
          readingLevel={readingLevel}
          readingLevelAEnabled={readingLevelAEnabled}
          sectionId={slideObject.modelId}
          showAIReadingLevelsForSection={undefined}
          studentLeveledReading={studentLeveledReading}
          onReadingLevelSelect={setReadingLevel}
        />
        {isAiGradeBand && (
          <AIReadingLevelGenerating
            generatePath={Routes.plato_api_generate_ai_reading_level_section_path(slideObject.modelId)}
            gradeBand={readingLevel}
            sectionId={slideObject.modelId}
            onSuccess={() => {
              queryClient.invalidateQueries(['sectionId', slideObject.modelId, readingLevel]);
            }}
          />
        )}
      </>
    );
  }

  return (
    <>
      {sectionText && (
        <>
          {glossaryReady && (slideEl ? (
            createPortal(
              <DraggableToolbar
                isPresentMode
                hasMainIdeas={hasMainIdeas}
                hasReadingLevelB={sectionText.section_reading_level_translation.has_reading_level_b}
                isStaffer={isStaffer}
                locale={locale}
                programGradeBands={programGradeBands}
                programId={programId}
                readingLevel={readingLevel}
                sectionId={sectionText.section_reading_level_translation.section_id}
                showAIReadingLevelsForSection={sectionText.section_reading_level_translation.show_ai_reading_levels}
                studentLeveledReading={studentLeveledReading}
                onReadingLevelSelect={setReadingLevel}
              />,
              slideEl,
            )
          ) : (
            <DraggableToolbar
              hasMainIdeas={hasMainIdeas}
              hasReadingLevelB={sectionText.section_reading_level_translation.has_reading_level_b}
              isStaffer={isStaffer}
              locale={locale}
              programGradeBands={programGradeBands}
              programId={programId}
              readingLevel={readingLevel}
              sectionId={sectionText.section_reading_level_translation.section_id}
              showAIReadingLevelsForSection={sectionText.section_reading_level_translation.show_ai_reading_levels}
              studentLeveledReading={studentLeveledReading}
              onReadingLevelSelect={setReadingLevel}
            />
          ))}
          <div
            ref={sectionRef}
            className="section_text_content slide-reading-section-text"
            id="text_to_speech_start"
            role="document"
            onClick={handleTextSectionClick}
          >
            <div
              className="fr-view tw-h-auto tw-w-auto"
              dangerouslySetInnerHTML={{ __html: sectionText.section_reading_level_translation.body_html }}
            />
          </div>
          {!isLoadingHighlights && translationId && userId && (
            <TextHighlighter
              containerSelector=".slide-reading-section-text"
              editPath={Routes.plato_api_highlight_path('highlight_id')}
              locale={locale}
              modelId={translationId}
              modelType="SectionReadingLevelTranslation"
              postPath={Routes.plato_api_highlights_path()}
              previousHighlights={highlights || []}
              scale={resizerScaleValue || 1}
              styles="tw-flex tw-justify-center tw-items-center tw-rounded tw-border-none tw-bg-opacity-10
                tw-bg-white tw-cursor-pointer tw-w-7 tw-p-0.5"
              userId={userId}
              userType={userType}
            />
          )}
        </>
      )}
      {interactable && (<ReadingSelectModal modalManager={modalManager} slideObject={slideObject} />)}
    </>
  );
};

export default SlideObjectReadingSection;
